import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Loader from "./Loader";
import DeductionManager from "./DeductionManager";
import { getBookingFinancials, monthIST } from "../utils/bookingFinancials";
import { apiUrl } from "./LoginSignup";
import {
  getBookingDeductionRowsForStats,
} from "../utils/bookingRevenue";

const adminRoles = ["admin", "senior admin", "super admin", "director", "dev", "srdev", "sr dev"];
const TERM_KEYS = Array.from({ length: 10 }, (_, index) => `term_${index + 1}`);

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
};

const monthKeyFromDate = (value) => value ? monthIST(value) : '';

const monthLabel = (monthKey) => {
  if (!monthKey || monthKey === "all") return "All Time";
  const [year, month] = String(monthKey).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString("default", { month: "long", year: "numeric" });
};

const getInitials = (name = "") =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("")
    .toUpperCase() || "NA";

const Scorecard = () => {
  const theme = useTheme();
  const session = JSON.parse(localStorage.getItem("userSession")) || {};
  const isAdmin = adminRoles.includes((session.user_role || "").toLowerCase());
  const currentMonthKey = monthKeyFromDate(new Date());

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [scopeMode, setScopeMode] = useState(isAdmin ? "all" : "month");
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deductionDialog, setDeductionDialog] = useState({ open: false, bookingId: '' });
  const [reload, setReload] = useState(0);
  const canRecover = ['director', 'dev', 'srdev', 'sr dev'].includes(String(session.user_role || '').trim().toLowerCase());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const headers = { authorization: session.token || "" };
        const bookingsUrl = `${apiUrl}/booking/scorecard-source`;
        const [bookingsRes, usersRes] = await Promise.all([
          fetch(bookingsUrl, { headers }),
          // Historical employee revenue must remain visible after an account is disabled.
          // This opt-in is restricted server-side to higher reporting roles.
          isAdmin ? fetch(`${apiUrl}/user/options?includeDisabled=true`, { headers }) : Promise.resolve(null),
        ]);

        const bookingsData = await bookingsRes.json().catch(() => ({}));
        if (!bookingsRes.ok) throw new Error(bookingsData.message || 'Scorecard could not load. Please retry.');
        setBookings(bookingsData.Allbookings || bookingsData.bookings || bookingsData || []);

        if (isAdmin && usersRes?.ok) {
          const usersData = await usersRes.json().catch(() => ({}));
          setUsers(Array.isArray(usersData.users) ? usersData.users : []);
        } else {
          setUsers([]);
        }
      } catch (error) {
        setLoadError(error.message || 'Scorecard could not load. Please retry.');
        setBookings([]);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, session.token, session.user_id, reload]);

  const allMonthOptions = useMemo(() => {
    const keys = new Set();
    bookings.forEach((booking) => {
      [booking.payment_date, booking.date, booking.createdAt].forEach((value) => {
        const key = monthKeyFromDate(value);
        if (key) keys.add(key);
      });
      (booking.deduction_recoveries || []).forEach((event) => { const key = monthKeyFromDate(event.effective_date); if (key) keys.add(key); });
      TERM_KEYS.forEach((termKey) => {
        const key = monthKeyFromDate(booking?.term_shares?.[termKey]?.payment_date);
        if (key) keys.add(key);
      });
      (booking.refund_adjustments || []).forEach((refund) => {
        const key = monthKeyFromDate(refund.refund_date || refund.created_at);
        if (key) keys.add(key);
      });
    });
    const values = [...keys].sort().reverse();
    if (!values.length) values.push(currentMonthKey);
    return values;
  }, [bookings, currentMonthKey]);

  useEffect(() => {
    if (!allMonthOptions.includes(selectedMonth)) {
      setSelectedMonth(allMonthOptions[0] || currentMonthKey);
    }
  }, [allMonthOptions, selectedMonth, currentMonthKey]);

  const employeePool = useMemo(() => {
    if (isAdmin) return users;
    return [{
      _id: session.user_id,
      name: session.name || "My Scorecard",
      role: session.user_role || "",
      profilePicture: session.profilePicture || "",
    }];
  }, [isAdmin, users, session.user_id, session.name, session.user_role, session.profilePicture]);

  const employeeCards = useMemo(() => {
    const activeMonth = scopeMode === 'all' ? 'all' : selectedMonth;
    const include = (term) => activeMonth === 'all' || monthKeyFromDate(term.payment_date) === activeMonth;
    const byEmployee = new Map();
    const add = (id, row) => {
      const key = String(id);
      if (!byEmployee.has(key)) byEmployee.set(key, []);
      byEmployee.get(key).push(row);
    };
    // Calculate and index each booking once, rather than once per employee.
    bookings.forEach((booking) => {
      getBookingFinancials(booking).terms.forEach((term) => {
        if (!include(term)) return;
        term.base_participants.forEach((person) => add(person.user_id, { type: 'Revenue', date: term.payment_date, bookingId: booking._id, companyName: booking.company_name || '-', clientName: booking.contact_person || '-', service: (booking.services || []).join(', '), amount: person.amount, tone: 'success', refundableType: booking.is_approval_refundable ? 'approval' : 'none', note: `Credit before deductions from ${term.term_key.replace('_', ' ').toUpperCase()} · ${term.payment_mode} · Term GST ${formatCurrency(term.gst)}` }));
      });
      getBookingDeductionRowsForStats(booking, include).forEach((row) => add(row.employeeId, { type: row.type, date: row.date, bookingId: row.bookingId, companyName: row.companyName, clientName: row.clientName, service: row.service, amount: row.deduction, tone: row.type === 'Refundable Clause Deduction' ? 'warning' : 'error', note: row.note || row.type }));
    });
    return employeePool.map((employee) => {
      const rows = (byEmployee.get(String(employee._id)) || []).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      const total = (predicate) => Math.round(rows.filter(predicate).reduce((sum, row) => sum + Number(row.amount || 0), 0) * 100) / 100;
      const revenue = total((row) => row.type === 'Revenue');
      const deductions = total((row) => row.type !== 'Revenue');
      return { ...employee, rows, revenue, deductions, net: Math.round((revenue - deductions) * 100) / 100,
        serviceDeductions: total((row) => ['Service Deduction', 'Historical Deduction Recovery', 'Deduction Recovery Reversal'].includes(row.type)),
        refundable: total((row) => row.type === 'Refundable Clause Deduction'), manualRefunds: total((row) => row.type === 'Manual Refund Adjustment'),
        approvalGuaranteeTerms: rows.filter((row) => row.type === 'Revenue' && row.refundableType === 'approval').length,
        bookingCount: new Set(rows.map((row) => row.bookingId).filter(Boolean)).size };
    }).filter((employee) => employee.rows.length > 0 || String(employee._id) === String(session.user_id)).sort((a, b) => b.net - a.net);
  }, [bookings, employeePool, scopeMode, selectedMonth, session.user_id]);

  useEffect(() => { setSelectedEmployee((current) => current ? employeeCards.find((employee) => String(employee._id) === String(current._id)) || null : null); }, [employeeCards]);

  const dashboardStats = useMemo(() => {
    const totals = employeeCards.reduce((acc, item) => {
      acc.revenue += item.revenue;
      acc.deductions += item.deductions;
      acc.serviceDeductions += item.serviceDeductions;
      acc.refundable += item.refundable;
      acc.manualRefunds += item.manualRefunds;
      acc.approvalGuaranteeTerms += item.approvalGuaranteeTerms;
      acc.net += item.net;
      return acc;
    }, { revenue: 0, deductions: 0, serviceDeductions: 0, refundable: 0, manualRefunds: 0, approvalGuaranteeTerms: 0, net: 0 });

    return [
      { label: "Net Revenue", value: totals.net, sub: "After all cuts", icon: <InsightsOutlinedIcon fontSize="small" />, color: "#0f766e" },
      { label: "Credit before deductions", value: totals.revenue, sub: "Before reversals", icon: <CurrencyRupeeOutlinedIcon fontSize="small" />, color: "#2563eb" },
      { label: "Service Deductions", value: totals.serviceDeductions, sub: "Vendor costs only", icon: <PaidOutlinedIcon fontSize="small" />, color: "#ea580c" },
      { label: "Auto Refundable Cuts", value: totals.refundable, sub: "Disbursement guarantee only", icon: <ReceiptLongOutlinedIcon fontSize="small" />, color: "#db2777" },
      { label: "Refund Adjustments", value: totals.manualRefunds, sub: "Manual refunds only", icon: <ReceiptLongOutlinedIcon fontSize="small" />, color: "#dc2626" },
      { label: "Approval Guarantee Terms", value: totals.approvalGuaranteeTerms, sub: "No automatic cut", icon: <ReceiptLongOutlinedIcon fontSize="small" />, color: "#7c3aed", format: "count" },
    ];
  }, [employeeCards]);

  const surfaceSx = {
    borderRadius: "8px",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: theme.palette.mode === "dark" ? "0 14px 30px rgba(2,6,23,0.3)" : "0 14px 34px rgba(15,23,42,0.06)",
    background: theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.94) 100%)"
      : "#ffffff",
  };

  const employeeRowSx = {
    borderRadius: "8px",
    border: "1px solid",
    borderColor: theme.palette.mode === "dark" ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.14)",
    boxShadow: "none",
    background: theme.palette.mode === "dark"
      ? "linear-gradient(180deg, rgba(15,23,42,0.88) 0%, rgba(15,23,42,0.84) 100%)"
      : "linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(255,255,255,1) 100%)",
    transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
    "&:hover": {
      borderColor: theme.palette.mode === "dark" ? "rgba(148,163,184,0.24)" : "rgba(255,59,31,0.18)",
      boxShadow: theme.palette.mode === "dark" ? "0 10px 22px rgba(2,6,23,0.22)" : "0 10px 24px rgba(15,23,42,0.07)",
      transform: "translateY(-1px)",
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <Loader />
      </Box>
    );
  }
  if (loadError) return <Alert severity="error">{loadError}<Button onClick={() => setReload((value) => value + 1)}>Retry scorecard</Button></Alert>;

  return (
    <Box sx={{ maxWidth: 1560, mx: "auto", width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <InsightsOutlinedIcon sx={{ color: "#14b8a6" }} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Scorecard
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Credit before deductions, applied deductions and final employee scores.
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { xs: "100%", sm: "auto" } }}>
          {isAdmin && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Scope</InputLabel>
              <Select label="Scope" value={scopeMode} onChange={(e) => setScopeMode(e.target.value)}>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="month">Month Wise</MenuItem>
              </Select>
            </FormControl>
          )}
          {(scopeMode === "month" || !isAdmin) && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Month</InputLabel>
              <Select label="Month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {allMonthOptions.map((month) => (
                  <MenuItem key={month} value={month}>{monthLabel(month)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Chip
            label={scopeMode === "all" && isAdmin ? `${employeeCards.length} employees` : monthLabel(selectedMonth)}
            sx={{ borderRadius: "999px", fontWeight: 800 }}
          />
        </Stack>
      </Box>

      <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 1.75 }} sx={{ mb: 2.25 }}>
        {dashboardStats.map((card) => (
          <Grid item xs={6} md={3} key={card.label}>
            <Card sx={{ ...surfaceSx, height: "100%" }}>
              <CardContent sx={{ p: { xs: 1.3, sm: 1.5 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}>
                      {card.label}
                    </Typography>
                    <Typography sx={{ mt: 0.45, fontWeight: 800, color: card.color, fontSize: { xs: "0.95rem", sm: "1.25rem" } }}>
                      {card.format === "count" ? Number(card.value || 0).toLocaleString("en-IN") : formatCurrency(card.value)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {card.sub}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 36, height: 36, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: `${card.color}18`, color: card.color }}>
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ ...surfaceSx, p: { xs: 1.2, sm: 1.5, md: 1.8 } }}>
        <Box sx={{ mb: 1.6 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Employee Scorecards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lower roles see only their selected month. Higher roles can switch between all-time and month-wise history.
          </Typography>
        </Box>

        <Stack spacing={1.25}>
          {employeeCards.length === 0 && (
            <Paper sx={{ p: 2, borderRadius: "8px", textAlign: "center" }}>
              <Typography color="text.secondary">No scorecard entries found.</Typography>
            </Paper>
          )}

          {employeeCards.map((employee) => {
            return (
              <Paper
                key={employee._id || employee.name}
                sx={{
                  ...employeeRowSx,
                  p: { xs: 1.15, sm: 1.35, md: 1.45 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flexWrap: "wrap",
                  }}
                >
                  <Avatar
                    src={employee.profilePicture || ""}
                    sx={{ width: 44, height: 44, bgcolor: "rgba(59,130,246,0.12)", color: "#2563eb", fontWeight: 800 }}
                  >
                    {getInitials(employee.name)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: "0.96rem", sm: "1rem" } }} noWrap>
                      {employee.name || "Employee"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {(employee.role || "").toUpperCase() || "TEAM MEMBER"}
                    </Typography>
                    {employee.isDisabled && (
                      <Chip
                        size="small"
                        label="Disabled"
                        color="warning"
                        sx={{ mt: 0.55, height: 20, fontWeight: 800 }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                      gap: 1,
                      width: { xs: "100%", lg: "auto" },
                      minWidth: { lg: 560 },
                    }}
                  >
                    {[
                      { label: "Bookings", value: employee.bookingCount, color: "#334155" },
                      { label: "Credit before deductions", value: formatCurrency(employee.revenue), color: "#059669" },
                      { label: "Deductions", value: formatCurrency(employee.deductions), color: "#ea580c" },
                      { label: "Net", value: formatCurrency(employee.net), color: "#2563eb" },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          borderRadius: "8px",
                          px: 1.1,
                          py: 0.9,
                          bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#ffffff",
                          border: "1px solid",
                          borderColor: theme.palette.mode === "dark" ? "rgba(148,163,184,0.14)" : "rgba(148,163,184,0.12)",
                        }}
                      >
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, color: item.color, fontSize: { xs: "0.82rem", sm: "0.9rem" } }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                    onClick={() => setSelectedEmployee(employee)}
                    sx={{ borderRadius: "8px", fontWeight: 800, minWidth: { xs: "100%", sm: 140 } }}
                  >
                    View History
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      {canRecover && <Button variant="outlined" onClick={() => setDeductionDialog({ open: true, bookingId: '' })}>Audit booking deductions</Button>}
      <DeductionManager open={deductionDialog.open} bookingId={deductionDialog.bookingId} onClose={() => setDeductionDialog({ open: false, bookingId: '' })} onChanged={() => setReload((value) => value + 1)} />
      <Dialog
        open={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {selectedEmployee?.name || "Employee"} Scorecard History
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmployee && (
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 1,
                  mb: 1,
                }}
              >
                {[
                  { label: "Bookings", value: selectedEmployee.bookingCount, color: "#334155" },
                  { label: "Credit before deductions", value: formatCurrency(selectedEmployee.revenue), color: "#059669" },
                  { label: "Deductions", value: formatCurrency(selectedEmployee.deductions), color: "#ea580c" },
                  { label: "Net", value: formatCurrency(selectedEmployee.net), color: "#2563eb" },
                ].map((item) => (
                  <Paper key={item.label} variant="outlined" sx={{ p: 1.1, borderRadius: "8px" }}>
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 700, textTransform: "uppercase" }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: item.color, mt: 0.35 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              {selectedEmployee.rows.length === 0 && (
                <Typography color="text.secondary">No scorecard entries found for this scope.</Typography>
              )}

              {selectedEmployee.rows.map((row, rowIndex) => (
                <Paper
                  key={`${selectedEmployee._id}-${row.bookingId}-${row.type}-${rowIndex}`}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: "8px",
                    borderColor: theme.palette.mode === "dark" ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.14)",
                    boxShadow: "none",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.8, flexWrap: "wrap", alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={row.type}
                        sx={{
                          borderRadius: "999px",
                          fontWeight: 800,
                          bgcolor: row.tone === "success" ? "rgba(16,185,129,0.12)" : row.tone === "warning" ? "rgba(249,115,22,0.12)" : "rgba(225,29,72,0.12)",
                          color: row.tone === "success" ? "#059669" : row.tone === "warning" ? "#ea580c" : "#e11d48",
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {formatDate(row.date)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 900, color: row.type === "Revenue" ? "#059669" : "#e11d48" }}>
                      {row.type === "Revenue" ? formatCurrency(row.amount) : `- ${formatCurrency(row.amount)}`}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1fr)" },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{row.companyName}</Typography>
                      <Typography variant="body2" color="text.secondary">{row.clientName}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700 }}>Service</Typography>
                      <Typography variant="body2">{row.service}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", fontWeight: 700 }}>Note</Typography>
                      <Typography variant="body2">{row.note}</Typography>
                      <Button size="small" onClick={() => setDeductionDialog({ open: true, bookingId: row.bookingId })}>Deduction details</Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEmployee(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Scorecard;
